import React, { useEffect, useMemo, useState } from 'react';
import { Container, Typography, Paper, Box, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, RadioGroup, FormControlLabel, Radio, TextField, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Menu from '../components/Menu';
import { QRCode } from 'react-qrcode-logo';
import api from '../utils/apiClient';
import { useNotifications } from '../context/NotificationContext';
import { getErrorMessage } from '../utils/errorMessage';
import { getLocale } from '../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import { API_BASE as BILL_API_BASE_URL } from '../utils/apiConfig';
import { resolveBillName } from '../utils/billName';
import { fetchAllBills } from '../utils/fetchAllBills';
import { getBillSourceCaption } from '../utils/billSource';
import { formatBillAmount, formatRubAmount } from '../utils/billAmount';
import { localizeItemName } from '../utils/billItemName';
import { getBillItemDisplay, getBillItemsTotal, isCustomBillType } from '../utils/billItemDisplay';

const Bill = () => {
  const { t } = useTranslation('billing');
  const { showError } = useNotifications();
  const theme = useTheme();
  const thisYear = new Date().getFullYear();
  const [currentYear, setCurrentYear] = useState(thisYear);
  const [rows, setRows] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [dialogStep, setDialogStep] = useState('details');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const [creatingPayment, setCreatingPayment] = useState(false);
  // Кошелёк — исполнителя счёта (WAYMORR или хостинг), его отдаёт сервер; без кошелька USDT не предлагается
  const walletAddress = selectedBill?.usdtWallet || '';
  const isUsdBill = selectedBill?.currency === 'USD';
  const [expiresAt, setExpiresAt] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [paymentAttempts, setPaymentAttempts] = useState([]);
  const [usdtPaymentCreated, setUsdtPaymentCreated] = useState(false);
  // Курс USDT→RUB приходит только с сервера. Раньше здесь стояло 118.5 «по умолчанию», и при сбое
  // загрузки курса клиенту показывалась сумма к оплате по выдуманному курсу.
  const [exchangeRateRubPerUSDT, setExchangeRateRubPerUSDT] = useState(null);
  const rateKnown = Number(exchangeRateRubPerUSDT) > 0 || selectedBill?.currency === 'USD';

  const parseRubAmount = (rubString) => {
    if (!rubString) return 0;
    const normalized = String(rubString).replace(/[^0-9.,]/g, '').replace(',', '.');
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };


  const toRuStatus = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'pending') return t('statusPending');
    if (v === 'paid' || v === 'success' || v === 'completed') return t('statusPaid');
    if (v === 'overdue' || v === 'expired') return t('statusOverdue');
    if (v === 'waiting') return t('statusWaiting');
    if (v.includes('check')) return t('statusChecking');
    if (v === 'create' || v === 'start') return t('statusCreated');
    return s || '—';
  };

  const getRawStatus = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'pending') return 'pending';
    if (v === 'paid' || v === 'success' || v === 'completed') return 'paid';
    if (v === 'overdue' || v === 'expired') return 'overdue';
    if (v === 'waiting') return 'waiting';
    if (v.includes('check')) return 'checking';
    if (v === 'create' || v === 'start') return 'created';
    return v;
  };

  const toRuMethod = (m) => {
    const v = String(m || '').toLowerCase();
    if (v === 'rub' || v === 'rur') return t('methodRub');
    if (v === 'usdt') return t('methodUsdt');
    if (v === 'crypto') return t('methodCrypto');
    if (v === 'card') return t('methodCard');
    if (v === 'bank_transfer') return t('methodBankTransfer');
    return t('methodOther');
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(getLocale());
    } catch (_) {
      return String(iso);
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString(getLocale());
    } catch (_) {
      return String(iso);
    }
  };

  const fetchUserBills = async () => {
    try {
      const savedUserData = localStorage.getItem('userData');
      const user = savedUserData ? JSON.parse(savedUserData) : null;
      const userId = user?.id || user?.user_id || null;
      const list = await fetchAllBills(api, `${BILL_API_BASE_URL}bill`, userId ? { user_id: userId } : {});
      const mapped = list.map((b) => {
        const billType = String(b.type || '').toLowerCase();
        const totalAmount = typeof b.amount === 'number' ? b.amount : Number(b.amount || 0);
        const itemsRaw = Array.isArray(b?.content) ? b.content : b?.content?.items;
        const items = Array.isArray(itemsRaw) ? itemsRaw : [];
        const paymentsRaw = Array.isArray(b?.payments) ? b.payments : [];
        const attempts = paymentsRaw.map((p) => {
          const method = String(p?.method || '').toLowerCase();
          const amountRub = Number(p?.amount_rub || 0);
          const amountUsdt = Number(p?.amount_usdt || 0);
          const createdIso = p?.date_create || p?.created_at || p?.createdAt;
          const amountStr = amountUsdt > 0 && (method === 'usdt' || method === 'crypto')
            ? `${amountUsdt} USDT`
            : (amountRub > 0 ? formatRubAmount(amountRub, t) : (amountUsdt > 0 ? `${amountUsdt} USDT` : '-'));
          return {
            id: p?.id,
            time: formatDateTime(createdIso),
            createdAtIso: createdIso,
            method: toRuMethod(p?.method),
            rawMethod: String(p?.method || ''),
            amount: amountStr,
            status: toRuStatus(p?.status),
            rawStatus: String(p?.status || ''),
            statusKey: getRawStatus(p?.status),
            course: Number(p?.course || 0),
          };
        });
        const sourceLabel = getBillSourceCaption(b, t);
        const isWaymorrSource = ['subscription', 'hoster_subscription'].includes(billType);
        return {
          id: b.id || b.uuid || b._id || String(b.name || Math.random()),
          name: b.name || b.title || '',
          nameKey: b.name_key || null,
          nameParams: b.name_params || null,
          fallback: b.name || b.title || t('labelInvoice'),
          status: toRuStatus(b.status),
          rawStatus: getRawStatus(b.status),
          amount: formatBillAmount(totalAmount, b.currency, t),
          rawAmount: totalAmount,
          // счёт по кВт·ч у хостера имеет тип service, а позиции у него те же: асик, часы, кВт·ч
          rawType: b?.content?.method === 'kwh' ? 'monitoring' : billType,
          currency: String(b.currency || 'RUB').toUpperCase(),
          issuedAt: formatDate(b.date_create || b.created_at || b.issued_at),
          issuedYear: new Date(b.date_create || b.created_at || b.issued_at || Date.now()).getFullYear(),
          paidAt: b.date_payment ? formatDate(b.date_payment) : (String(b.status || '').toLowerCase() === 'paid' ? formatDate(b.paid_at) : '-'),
          items,
          attempts,
          hosting_usdt_wallet: b.hosting_usdt_wallet || null,
          usdtWallet: b.usdt_wallet || null,
          usdtAvailable: b.usdt_available === true,
          sourceKey: isWaymorrSource ? 'waymorr' : 'hoster',
          sourceLabel,
        };
      });
      setRows(mapped);
    } catch (e) {
      console.error('Bill: ошибка загрузки счетов', e);
    }
  };

  const fetchCourse = async () => {
    try {
      console.log('Bill: fetching USDT course...');
      const res = await api.get(`${BILL_API_BASE_URL}course`);
      console.log('Bill: fetched course response', res?.data);
      const rawCourseObj = res?.data?.course;
      const value = Number(
        (rawCourseObj && (rawCourseObj.course ?? rawCourseObj.value)) ??
        res?.data?.course ??
        res?.data?.rate ??
        res?.data?.rub_per_usdt ??
        res?.data?.RUB_per_USDT ??
        res?.data?.usdt ??
        0
      );
      if (Number.isFinite(value) && value > 0) {
        console.log('Bill: parsed USDT rate', value);
        setExchangeRateRubPerUSDT(value);
        return value;
      }
      console.warn('Bill: invalid course value from server, using existing rate', value);
      return exchangeRateRubPerUSDT;
    } catch (e) {
      console.error('Bill: ошибка загрузки курса', e);
      return exchangeRateRubPerUSDT;
    }
  };

  const computedUsdtAmount = useMemo(() => {
    if (!selectedBill) return '0.00';
    // Долларовый счёт — один к одному; рублёвый — по курсу
    if (selectedBill.currency === 'USD') return Number(selectedBill.rawAmount || 0).toFixed(2);
    const rub = parseRubAmount(selectedBill.amount);
    const rate = Number(exchangeRateRubPerUSDT) || 0;
    if (rate <= 0) return '0.00';
    const usdt = rub / rate;
    return usdt.toFixed(2);
  }, [selectedBill, exchangeRateRubPerUSDT]);

  const getStatusColor = (rawStatus) => {
    switch (rawStatus) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'checking':
        return 'info';
      case 'waiting':
        return 'info';
      case 'created':
        return 'default';
      default:
        return 'default';
    }
  };

  const updateBillStatusLocally = (billId, statusLabel, rawStatusKey) => {
    if (!billId || !statusLabel) return;
    setRows((prev) => prev.map((r) => (r.id === billId ? { ...r, status: statusLabel, rawStatus: rawStatusKey || r.rawStatus } : r)));
    setSelectedBill((prev) => (prev && prev.id === billId ? { ...prev, status: statusLabel, rawStatus: rawStatusKey || prev.rawStatus } : prev));
  };

  const formatAttemptForUi = (attempt) => {
    const createdIso = attempt?.date_create || attempt?.created_at || attempt?.createdAt || attempt?.createdAtIso || new Date().toISOString();
    const rawMethod = String(attempt?.method || attempt?.rawMethod || '');
    const id = attempt?.id;
    return ({
      id,
      time: attempt?.time || formatDateTime(createdIso),
      createdAtIso: createdIso,
      method: toRuMethod(rawMethod),
      rawMethod,
      amount: attempt?.amount || (() => {
        const method = String(attempt?.method || '').toLowerCase();
        const amountRub = Number(attempt?.amount_rub || 0);
        const amountUsdt = Number(attempt?.amount_usdt || 0);
        return amountUsdt > 0 && (method === 'usdt' || method === 'crypto')
          ? `${amountUsdt} USDT`
          : (amountRub > 0 ? formatRubAmount(amountRub, t) : (amountUsdt > 0 ? `${amountUsdt} USDT` : '-'));
      })(),
      status: toRuStatus(attempt?.status || attempt?.rawStatus),
      rawStatus: String(attempt?.status || attempt?.rawStatus || ''),
      statusKey: getRawStatus(attempt?.status || attempt?.rawStatus),
      course: Number(attempt?.course || 0),
    });
  };

  const createPaymentRecord = async ({ method, status, overrideRate }) => {
    if (!selectedBill) return null;
    try {
      setCreatingPayment(true);
      const billId = selectedBill.id;
      const rubAmount = isUsdBill ? null : parseRubAmount(selectedBill.amount);
      const rate = (overrideRate != null ? Number(overrideRate) : Number(exchangeRateRubPerUSDT)) || 0;
      if (method === 'USDT' && !isUsdBill && rate <= 0) {
        showError(t('courseUnavailable'));
        return null;
      }
      const usdtAmount = isUsdBill ? Number(Number(selectedBill.rawAmount || 0).toFixed(2)) : (rate > 0 ? Number((rubAmount / rate).toFixed(2)) : 0);
      const payload = {
        bill_id: billId,
        method: method,
        amount_rub: rubAmount,
        amount_usdt: method === 'USDT' ? usdtAmount : 0,
        course: method === 'USDT' ? rate : 0,
        status: status,
      };
      console.log('Bill: creating payment record', payload);
      const res = await api.post(`${BILL_API_BASE_URL}bill_payment`, payload);
      const raw = res?.data?.payment ?? res?.data ?? payload;
      // ensure required fields present
      const created = {
        id: raw?.id,
        bill_id: raw?.bill_id ?? billId,
        method: raw?.method ?? method,
        amount_rub: raw?.amount_rub ?? payload.amount_rub,
        amount_usdt: raw?.amount_usdt ?? payload.amount_usdt,
        course: raw?.course ?? payload.course,
        status: raw?.status ?? status,
        date_create: raw?.date_create ?? new Date().toISOString(),
      };
      console.log('Bill: payment record created', created);
      const attemptUi = formatAttemptForUi(created);
      // de-duplicate existing 'create' attempt of same method in local state
      setPaymentAttempts((prev) => {
        const filtered = prev.filter((a) => !(String(a?.rawStatus || '').toLowerCase() === 'create' && String(a?.rawMethod || '').toLowerCase() === String(created.method || '').toLowerCase()));
        return [attemptUi, ...filtered];
      });
      setRows((prev) => prev.map((r) => {
        if (r.id !== billId) return r;
        const current = Array.isArray(r.attempts) ? r.attempts : [];
        const filtered = current.filter((a) => !(String(a?.rawStatus || '').toLowerCase() === 'create' && String(a?.rawMethod || '').toLowerCase() === String(created.method || '').toLowerCase()));
        return { ...r, attempts: [attemptUi, ...filtered] };
      }));
      // update bill status label for USDT create
      if (String(created.method || '').toLowerCase() === 'usdt' && String(created.status || '').toLowerCase() === 'create') {
        // не обновляем статус в основной таблице для create
        setSelectedBill((prev) => (prev ? { ...prev, status: t('statusCreated'), rawStatus: 'created' } : prev));
      }
      return created;
    } catch (err) {
      console.error('Bill: ошибка создания записи оплаты', err);
      return null;
    } finally {
      setCreatingPayment(false);
    }
  };

  const updatePaymentStatus = async (paymentId, status) => {
    if (!paymentId) return false;
    try {
      setCreatingPayment(true);
      console.log('Bill: updating payment status', { paymentId, status });
      const res = await api.put(`${BILL_API_BASE_URL}bill_payment/${paymentId}`, { status });
      console.log('Bill: payment status updated', res?.data);
      // Update UI attempts
      setPaymentAttempts((prev) => prev.map((a) => (a.id === paymentId ? { ...a, rawStatus: status, statusKey: getRawStatus(status), status: toRuStatus(status) } : a)));
      if (selectedBill?.id) {
        setRows((prev) => prev.map((r) => (r.id === selectedBill.id ? { ...r, attempts: (r.attempts || []).map((a) => (a.id === paymentId ? { ...a, rawStatus: status, statusKey: getRawStatus(status), status: toRuStatus(status) } : a)) } : r)));
        if (String(status).toLowerCase() === 'pending') {
          updateBillStatusLocally(selectedBill.id, t('statusWaiting'), 'waiting');
        }
      }
      return true;
    } catch (e) {
      console.error('Bill: ошибка обновления статуса оплаты', e);
      return false;
    } finally {
      setCreatingPayment(false);
    }
  };

  const handleRowClick = (row) => {
    setSelectedBill(row);
    setDialogStep('details');
    setPaymentMethod('');
    setExpiresAt(null);
    setRemainingMs(0);
    setPaymentAttempts(Array.isArray(row?.attempts) ? row.attempts : []);
    setUsdtPaymentCreated(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedBill(null);
    setDialogStep('details');
    setPaymentMethod('');
    setExpiresAt(null);
    setRemainingMs(0);
  };

  useEffect(() => {
    if (dialogStep === 'payment' && paymentMethod === 'usdt') {
      console.log('Bill: USDT selected, initializing payment step');
      // Try to reuse existing 'create' attempt under 30 minutes
      const existingCreate = (paymentAttempts || []).find((a) => String(a?.rawStatus || a?.status || '').toLowerCase() === 'create' && String(a?.rawMethod || '').toLowerCase() === 'usdt');
      if (existingCreate && existingCreate.createdAtIso) {
        try {
          const createdAt = new Date(existingCreate.createdAtIso);
          const expiry = new Date(createdAt.getTime() + 30 * 60 * 1000);
          const now = new Date();
          const remaining = Math.max(0, expiry - now);
          console.log('Bill: found existing create attempt', { createdAt, expiry, remaining });
          setExpiresAt(expiry);
          setRemainingMs(remaining);
          const rateFromAttempt = Number(existingCreate.course || 0);
          if (rateFromAttempt > 0) {
            console.log('Bill: using course from existing attempt', rateFromAttempt);
            setExchangeRateRubPerUSDT(rateFromAttempt);
          }
          // отражаем статус "Создано" только в попапе, основную таблицу не меняем
          setSelectedBill((prev) => (prev ? { ...prev, status: t('statusCreated'), rawStatus: 'created' } : prev));
          setUsdtPaymentCreated(true);
          return;
        } catch (e) {
          console.warn('Bill: failed to parse existing create attempt timestamp', e);
        }
      }
      // Otherwise start new 30-min session with fresh course and create record
      (async () => {
        try {
          console.log('Bill: starting new USDT session, fetching course...');
          const rate = await fetchCourse();
          const expiry = new Date(Date.now() + 30 * 60 * 1000);
          setExpiresAt(expiry);
          setRemainingMs(expiry - new Date());
          if (!usdtPaymentCreated) {
            const created = await createPaymentRecord({ method: 'USDT', status: 'create', overrideRate: rate });
            // отражаем статус "Создано" только в попапе, основную таблицу не меняем
            setSelectedBill((prev) => (prev ? { ...prev, status: t('statusCreated'), rawStatus: 'created' } : prev));
            setUsdtPaymentCreated(true);
          }
        } catch (e) {
          console.error('Bill: error during USDT init', e);
        }
      })();
    } else {
      setExpiresAt(null);
      setRemainingMs(0);
      setUsdtPaymentCreated(false);
    }
  }, [dialogStep, paymentMethod]);

  useEffect(() => {
    void fetchUserBills();
  }, []);

  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => {
      const ms = Math.max(0, expiresAt - new Date());
      setRemainingMs(ms);
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const remainingTime = useMemo(() => {
    const total = Math.max(0, remainingMs);
    const minutes = Math.floor(total / 60000);
    const seconds = Math.floor((total % 60000) / 1000);
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [remainingMs]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopyTooltipOpen(true);
      setTimeout(() => setCopyTooltipOpen(false), 1200);
    } catch (_) {}
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadPdf = async (billId) => {
    if (!billId) return;
    try {
      setDownloadingPdf(true);
      const res = await api.get(`${BILL_API_BASE_URL}bill/${billId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bill_${billId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.response?.status === 412) {
        showError(t('requisitesMissing'));
      } else {
        console.error('Bill: ошибка скачивания PDF', e);
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedBill) return;
    if (paymentMethod === 'usdt') {
      console.log('Bill: confirming USDT payment');
      // find existing 'create' attempt for USDT
      let existingCreate = (paymentAttempts || []).find((a) => String(a?.rawStatus || '').toLowerCase() === 'create' && String(a?.rawMethod || '').toLowerCase() === 'usdt');
      if (!existingCreate) {
        // if not present, create it first with current rate
        const created = await createPaymentRecord({ method: 'USDT', status: 'create' });
        if (created?.id) {
          existingCreate = formatAttemptForUi({ ...created, method: 'USDT' });
        }
      }
      if (existingCreate?.id) {
        const ok = await updatePaymentStatus(existingCreate.id, 'pending');
        if (ok) {
          // закрываем popup и обновляем статус счета на "Ожидает подтверждения"
          updateBillStatusLocally(selectedBill.id, t('statusWaiting'), 'waiting');
          setDialogOpen(false);
        }
      } else {
        console.warn('Bill: USDT confirm clicked but no create attempt id found');
      }
      return;
    }
    // ЮKassa - редирект на страницу оплаты
    if (paymentMethod === 'card') {
      try {
        setCreatingPayment(true);
        const res = await api.post(`${BILL_API_BASE_URL}yukassa/create`, { bill_id: selectedBill.id });
        if (res?.data?.confirmation_url) {
          window.location.href = res.data.confirmation_url;
        } else {
          console.error('Bill: ЮKassa не вернула URL');
        }
      } catch (e) {
        console.error('Bill: ошибка создания платежа ЮKassa', e);
        const errMsg = getErrorMessage(e, t('paymentMethodUnavailable'));
        alert(errMsg);
      } finally {
        setCreatingPayment(false);
      }
      return;
    }
    // Расчетный счет или крипто
    const method = paymentMethod === 'bank_transfer' ? 'bank_transfer' : (paymentMethod === 'crypto' ? 'crypto' : 'other');
    console.log('Bill: confirming payment', { method });
    const created = await createPaymentRecord({ method, status: 'pending' });
    if (created) {
      updateBillStatusLocally(selectedBill.id, t('statusWaiting'), 'waiting');
      setDialogOpen(false);
    }
    setExpiresAt(null);
    setRemainingMs(0);
  };

  // Счета выбранного года. Неоплаченные счета прошлых лет остаются на виду в текущем году,
  // чтобы долг не прятался за переключателем.
  const visibleRows = useMemo(() => rows.filter((r) => r.issuedYear === currentYear
    || (currentYear === thisYear && r.issuedYear < thisYear && ['pending', 'overdue'].includes(r.rawStatus))),
  [rows, currentYear, thisYear]);
  const hasEarlierYear = useMemo(() => rows.some((r) => r.issuedYear < currentYear), [rows, currentYear]);

  return (
    <>
      <Menu />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h4" component="h1">
              {t('pageTitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, mb: 2 }}>
            <IconButton aria-label={t('previousYear')} disabled={!hasEarlierYear} onClick={() => setCurrentYear((y) => y - 1)}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" component="div">
              {currentYear}
            </Typography>
            <IconButton aria-label={t('nextYear')} disabled={currentYear >= thisYear} onClick={() => setCurrentYear((y) => y + 1)}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="medium" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('labelInvoice')}</TableCell>
                <TableCell>{t('labelPaymentStatus')}</TableCell>
                <TableCell>{t('labelIssuedDate')}</TableCell>
                <TableCell>{t('labelPaidDate')}</TableCell>
                <TableCell align="right">{t('labelAmount')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row, index) => (
                <TableRow
                  key={index}
                  hover
                  onClick={() => handleRowClick(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2">{resolveBillName({ name: row.name, name_key: row.nameKey, name_params: row.nameParams }, t) || row.fallback}</Typography>
                    {row.sourceLabel && (
                      <Typography variant="caption" color="text.secondary">{row.sourceLabel}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status} color={getStatusColor(row.rawStatus)} />
                  </TableCell>
                  <TableCell>{row.issuedAt}</TableCell>
                  <TableCell>{row.paidAt}</TableCell>
                  <TableCell align="right">{row.amount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
          <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
            <DialogTitle>{t('billDetails')}</DialogTitle>
            <DialogContent dividers>
              {selectedBill && (
                <>
                  {dialogStep === 'details' && (
                    <>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, rowGap: 1.5, columnGap: 2, mb: 2 }}>
                        <Typography color="text.secondary">{t('labelTitle')}</Typography>
                        <Typography>{resolveBillName({ name: selectedBill.name, name_key: selectedBill.nameKey, name_params: selectedBill.nameParams }, t) || selectedBill.fallback}</Typography>
                        {selectedBill.sourceLabel && (
                          <>
                            <Typography color="text.secondary">{t('billSourceLabel')}</Typography>
                            <Typography>{selectedBill.sourceLabel}</Typography>
                          </>
                        )}
                        <Typography color="text.secondary">{t('labelStatus')}</Typography>
                        <Box>
                          <Chip size="small" label={selectedBill.status} color={getStatusColor(selectedBill.rawStatus)} />
                        </Box>
                        <Typography color="text.secondary">{t('labelWhenIssued')}</Typography>
                        <Typography>{selectedBill.issuedAt}</Typography>
                        <Typography color="text.secondary">{t('labelWhenPaid')}</Typography>
                        <Typography>{selectedBill.paidAt}</Typography>
                        <Typography color="text.secondary">{t('labelTotalAmount')}</Typography>
                        <Typography>{selectedBill.amount}</Typography>
                      </Box>
                      <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 480 }}>
                        <TableHead>
                          <TableRow>
                            {isCustomBillType(selectedBill.rawType) ? (
                              <>
                                <TableCell>{t('labelItemName')}</TableCell>
                                <TableCell>{t('labelItemQuantity')}</TableCell>
                                <TableCell align="right">{t('labelAmount')}</TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell>{t('labelDevice')}</TableCell>
                                <TableCell>{t('labelWorker')}</TableCell>
                                <TableCell>{t('labelHoursCount')}</TableCell>
                                <TableCell align="right">{t('labelAmount')}</TableCell>
                              </>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(Array.isArray(selectedBill.items) ? selectedBill.items : []).map((it, idx) => {
                            const isCustom = isCustomBillType(selectedBill.rawType);
                            const display = getBillItemDisplay(it, selectedBill.rawType);
                            const label = isCustom
                              ? (display.label || '-')
                              : (localizeItemName(display.label, t) || t('labelDevice'));
                            const quantity = display.quantity != null ? display.quantity : '-';
                            return (
                              <TableRow key={idx}>
                                <TableCell>{label}</TableCell>
                                {!isCustom && <TableCell>{it.worker || '-'}</TableCell>}
                                <TableCell>{quantity}</TableCell>
                                <TableCell align="right">{formatBillAmount(display.amount, selectedBill.currency, t)}</TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow>
                            <TableCell colSpan={isCustomBillType(selectedBill.rawType) ? 2 : 3}><strong>{t('labelTotal')}</strong></TableCell>
                            <TableCell align="right">
                              <strong>
                                {formatBillAmount(
                                  getBillItemsTotal(selectedBill.items, selectedBill.rawType, selectedBill.rawAmount),
                                  selectedBill.currency,
                                  t
                                )}
                              </strong>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      </TableContainer>
                    </>
                  )}
                  {dialogStep === 'payment' && (
                    <Box sx={{ py: 2 }}>
                      <Typography variant="h6" gutterBottom>{t('payment')}</Typography>
                      <Typography sx={{ mb: 2 }}>{t('choosePaymentMethod')}</Typography>
                      <RadioGroup
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        sx={{ mb: 2 }}
                      >
                        {selectedBill?.usdtAvailable && <FormControlLabel value="usdt" control={<Radio />} label={t('paymentUsdt')} />}
                        <FormControlLabel value="card" control={<Radio />} label={t('paymentCard')} />
                        <FormControlLabel value="bank_transfer" control={<Radio />} label={t('paymentBankTransfer')} />
                        <FormControlLabel value="crypto" control={<Radio />} label={t('paymentCryptoOther')} />
                      </RadioGroup>

                      {paymentMethod === 'card' && (
                        <Box sx={{ mt: 1 }}>
                          <Typography color="text.secondary">
                            {t('cardPaymentDescription')}
                          </Typography>
                        </Box>
                      )}

                      {paymentMethod === 'bank_transfer' && (
                        <Box sx={{ mt: 1 }}>
                          <Typography sx={{ mb: 1 }}>
                            {t('bankTransferDescription')}
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            disabled={downloadingPdf}
                            onClick={() => handleDownloadPdf(selectedBill?.id)}
                            sx={{ mb: 1 }}
                          >
                            {t('downloadInvoicePdf')}
                          </Button>
                        </Box>
                      )}

                      {paymentMethod === 'crypto' && (
                        <Box sx={{ mt: 1 }}>
                          <Typography color="text.secondary">
                            {t('cryptoDescription')}
                          </Typography>
                        </Box>
                      )}

                      {paymentMethod === 'usdt' && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 2 }}>
                          <Typography color="error" variant="body2">
                            {t('usdtWarning')}
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              label={t('labelWallet')}
                              value={walletAddress}
                              InputProps={{ readOnly: true }}
                              fullWidth
                              size="small"
                            />
                            <Tooltip open={copyTooltipOpen} title={t('copied')} disableHoverListener>
                              <Button variant="outlined" onClick={copyToClipboard}>{t('copyButton')}</Button>
                            </Tooltip>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                            <Box sx={{ backgroundColor: 'transparent', p: 0, borderRadius: 0, overflow: 'visible', border: theme.palette.mode === 'light' ? '1px solid #eee' : 'none' }}>
                              <QRCode
                                value={`trc20:${walletAddress}`}
                                size={180}
                                quietZone={8}
                                fgColor={theme.palette.mode === 'dark' ? '#fff' : '#000'}
                                bgColor={'transparent'}
                                eyeRadius={4}
                                ecLevel="M"
                              />
                            </Box>
                            <Box>
                              {rateKnown
                                ? <Typography>{t('amountToPay')} <strong>{computedUsdtAmount} USDT</strong></Typography>
                                : <Typography color="error">{t('courseUnavailable')}</Typography>}
                              <Typography color="text.secondary">{t('timer')} {remainingTime}</Typography>
                            </Box>
                          </Box>

                          {remainingMs === 0 && (
                            <Typography color="error" variant="body2">
                              {t('rateExpired')}
                            </Typography>
                          )}

                          <Typography color="text.secondary" variant="body2">
                            {t('linkValidFor30Min')}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>{t('previousPaymentAttempts')}</Typography>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 480 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('labelTime')}</TableCell>
                              <TableCell>{t('labelMethod')}</TableCell>
                              <TableCell>{t('labelAmount')}</TableCell>
                              <TableCell>{t('labelStatus')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paymentAttempts.map((a, i) => (
                              <TableRow key={i}>
                                <TableCell>{a.time}</TableCell>
                                <TableCell>{a.method}</TableCell>
                                <TableCell>{a.amount}</TableCell>
                                <TableCell>
                                  <Chip size="small" label={a.status} color={getStatusColor(a.statusKey)} />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              {dialogStep === 'payment' && (
                <>
                  <Button onClick={() => setDialogStep('details')}>{t('labelBack')}</Button>
                  {paymentMethod && (
                    <Button variant="contained" color="primary" disabled={creatingPayment} onClick={handleConfirmPayment}>
                      {t('confirmPayment')}
                    </Button>
                  )}
                </>
              )}
              {dialogStep === 'details' && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    disabled={downloadingPdf}
                    onClick={() => handleDownloadPdf(selectedBill?.id)}
                  >
                    {t('downloadPdf')}
                  </Button>
                  {(selectedBill?.rawStatus === 'pending' || selectedBill?.rawStatus === 'overdue') && (
                    <Button variant="contained" color="primary" onClick={() => setDialogStep('payment')}>
                      {t('goToPayment')}
                    </Button>
                  )}
                </>
              )}
              <Button onClick={handleCloseDialog} variant="outlined">{t('labelClose')}</Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
    </>
  );
};

export default Bill;
